import { getRailsSortParameters } from '@pba/react-network';
import { keepPreviousData } from '@pba/react-query';
import {
  type FileAsset,
  type FolderAsset,
  FolderType,
  type Pagination,
  createFileAsset,
  createFolderAsset,
} from 'models';
import { useInfiniteQuery } from 'query';
import type { ApiClientResponseError, DefaultResponseErrorDataObject } from 'common/services';
import { listAssets } from '../services';
import { type AssetsQueryFilter, type AssetsQuerySorter, getAssetsInfiniteQueryKey } from './keys';

type AssetsQueryParams = {
  filter: AssetsQueryFilter;
  pagination?: Pick<Pagination, 'pageSize'>;
  sorter?: AssetsQuerySorter;
};

export const useAssetsQuery = ({ filter, pagination, sorter }: AssetsQueryParams) =>
  useInfiniteQuery<Array<FileAsset | FolderAsset>, number, ApiClientResponseError<DefaultResponseErrorDataObject>>({
    async queryFn({ pageParam: pageParameter }) {
      /* eslint-disable @typescript-eslint/naming-convention -- Start: We missing correct application model in camelCase, we need to fix it */
      const railsSortParameters = getRailsSortParameters({
        name: sorter?.name,
        size: sorter?.size,
        updated_at: sorter?.updatedAt,
      });
      const { data } = await listAssets({
        config: {
          params: {
            community_id: filter.communityId,
            limit: pagination?.pageSize,
            offset: pagination ? pageParameter : undefined,
            order: railsSortParameters.sort,
            parent_id: filter.parentId,
            program_id: filter.programId,
            sort: railsSortParameters.order,
          },
        },
      });
      /* eslint-enable @typescript-eslint/naming-convention -- End: We missing correct application model in camelCase, we need to fix it */

      return data.map((item) => (item.type === FolderType ? createFolderAsset(item) : createFileAsset(item)));
    },
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    getNextPageParam(lastPage, allPages) {
      if (!pagination) {
        return undefined;
      }

      return lastPage.length < pagination.pageSize ? undefined : allPages.length * pagination.pageSize;
    },
    queryKey: getAssetsInfiniteQueryKey({ filter, pagination, sorter }),
    throwOnError: true,
  });
